// ** React Imports
import { useContext } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import loadable from "@loadable/component";

// ** Custom Components
import Block from "components/layout/Block";
import UserAvatar from "components/user/UserAvatar";
import Login from "components/user/Login";
import Dropdown from "components/layout/Dropdown";
import { ButtonRoundedHeader } from "components/layout/Button/ButtonRoundedHeader";
import Loader from "components/layout/Loader";

// ** User Menu Items
const UserMenuContent = loadable(() => import("./components/UserMenuContent"), {
	ssr: false,
});

// ** Custom Hooks
import useOpenClose from "hooks/useOpenClose";
import useBreakpointWidth from "hooks/useBreakpointWidth";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";
import { DropdownTriggerContext } from "components/layout/Dropdown/DropdownTriggerContext";

// ** Constants
import { topHeaderHeight } from "styles/declares";

// #####################################################

const MAX_DESKTOP_WIDTH = "240px";

// #####################################################

const UserMenu = () => {
	const { isTablet, isPhone } = useContext(deviceTypeContext);

	const isClient = useSelector((state) => state.globalCommon.isClient);

	const userId = useSelector((state) => state.globalUser?.id);
	const userAvatarSrc = useSelector((state) => state.globalUser?.avatar);
	const userLogin = useSelector((state) => state.globalUser?.login);
	const userOnline = useSelector((state) => state.globalUser?.online);
	const userPrivateData = useSelector(
		(state) =>
			state.globalUser?.privateData || state.globalUser?.private_data
	);
	const userProfileStatus = useSelector(
		(state) => state.globalUser?.profileStatus
	);

	const { isOpen, close, extendedClose, extendedToggle } = useOpenClose();

	const breakpointWidth = useBreakpointWidth();

	const useLayerOptions = {
		onOutsideClick: () => {
			!isPhone && typeof close === "function" && extendedToggle();
		},
		placement: "bottom-end",
		triggerOffset: 10,
		container: isClient ? "app" : "root",
	};

	const customLayerStyle = {
		position: "fixed",
		width: MAX_DESKTOP_WIDTH,
		overflowY: "auto",
		right: isPhone ? `0` : `calc((100% - ${breakpointWidth}px)/2)`,
		left: "unset",
		top: isTablet
			? `${topHeaderHeight.tablet}px`
			: `${topHeaderHeight.desktop}px`,
		maxHeight: `calc((100dvh - ${topHeaderHeight.desktop}px) - var(--size-grid))`,
		zIndex: 999,
		borderTopRightRadius: 0,
		borderTopLeftRadius: 0,
	};

	// #####################################################

	return (
		<Dropdown
			isOpen={isOpen}
			close={(insideUseEffect) => {
				extendedClose(insideUseEffect);
			}}
			useLayerOptions={useLayerOptions}
			customLayerStyle={customLayerStyle}
			withMobileVersion
			isParentComponent={true}
			menuContent={() => (
				<UserMenuContent
					fallback={
						<Block flex justifyCenter alignCenter height={40}>
							<Loader size={20} />
						</Block>
					}
					extendedClose={extendedClose}
				/>
			)}
		>
			<DropdownTriggerContext.Consumer>
				{({ triggerProps }) => (
					<ButtonRoundedHeader
						onClick={extendedToggle}
						active={isOpen}
						label="Menu użytkownika"
						userProfile
						aria-haspopup="true"
						aria-expanded={isOpen.toString()}
						nopad
					>
						<Block
							flex
							{...triggerProps}
							style={{
								padding: isPhone || isTablet ? "0.5rem" : "6px",
								paddingTop: "0px",
								paddingBottom: "0px",
							}}
						>
							<UserAvatar
								userId={userId}
								borderSize
								src={userAvatarSrc}
								login={userLogin}
								online={userOnline}
								privateData={userPrivateData}
								status={userProfileStatus}
								size="xsmall"
							/>
							<Login
								maxWidthWithEllipsis={220}
								login={userLogin}
							/>
						</Block>
					</ButtonRoundedHeader>
				)}
			</DropdownTriggerContext.Consumer>
		</Dropdown>
	);
};

export default UserMenu;

// #####################################################
